// super-excalidraw: comments sidebar tab. Lists root threads + their full
// reply chains for the current diagram. Toggle the row to expand/collapse;
// resolved threads default to expanded so the whole conversation stays
// readable. The "↗" jump button scrolls to the anchor element on the canvas.

import React, { useEffect, useMemo, useState } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import {
  CommentDTO,
  formatRelativeTime,
  initialOf,
  listComments,
} from "../data/comments";

import "./CommentsTab.scss";

export type CommentsTabProps = {
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  diagramId: string | null;
  visible?: boolean;
};

const Avatar: React.FC<{ name?: string; size?: number }> = ({
  name,
  size = 30,
}) => {
  const hash =
    [...(name || "?")].reduce((a, c) => (a * 31 + c.charCodeAt(0)) | 0, 7) >>>
    0;
  const hue = hash % 360;
  return (
    <span
      className="comments-tab__avatar"
      style={{
        background: `linear-gradient(135deg, hsl(${hue}, 70%, 60%), hsl(${(hash >> 8) % 360}, 65%, 50%))`,
        width: size,
        height: size,
        fontSize: Math.max(11, size * 0.45),
      }}
      title={name}
    >
      {initialOf(name)}
    </span>
  );
};

const groupRoots = (items: CommentDTO[]) => {
  const replies = new Map<string, CommentDTO[]>();
  const roots: CommentDTO[] = [];
  for (const c of items) {
    if (!c.parentId) {
      roots.push(c);
    } else {
      const arr = replies.get(c.parentId) ?? [];
      arr.push(c);
      replies.set(c.parentId, arr);
    }
  }
  for (const arr of replies.values()) {
    arr.sort((a, b) => a.createdAt - b.createdAt);
  }
  return { roots, replies };
};

export const CommentsTab: React.FC<CommentsTabProps> = ({
  excalidrawAPI,
  diagramId,
  visible,
}) => {
  const [items, setItems] = useState<CommentDTO[] | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const refresh = async () => {
    if (!diagramId) {
      setItems([]);
      return;
    }
    setError(null);
    try {
      const r = await listComments(diagramId);
      setItems(r.items || []);
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setItems([]);
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [diagramId]);

  useEffect(() => {
    if (visible) {
      refresh();
    }
  }, [visible]);

  const { roots, replies } = useMemo(
    () => groupRoots(items ?? []),
    [items],
  );

  const filtered = roots
    .filter((r) => showResolved || !r.resolved)
    .sort((a, b) => b.createdAt - a.createdAt);

  // Resolved threads default to expanded (so the whole conversation stays
  // visible when you flip "Show resolved" on); user-toggled state wins.
  const isExpanded = (id: string, resolved: boolean) => {
    if (id in expanded) {
      return expanded[id];
    }
    return resolved;
  };

  const toggle = (id: string, resolved: boolean) => {
    setExpanded((prev) => ({
      ...prev,
      [id]: !isExpanded(id, resolved),
    }));
  };

  const onJump = (c: CommentDTO) => {
    if (!excalidrawAPI || !c.elementId) {
      return;
    }
    const el = excalidrawAPI
      .getSceneElements()
      .find((e: any) => e.id === c.elementId);
    if (!el) {
      return;
    }
    excalidrawAPI.scrollToContent(el as any, {
      fitToViewport: true,
      animate: true,
      duration: 300,
    } as any);
    excalidrawAPI.updateScene({
      appState: {
        ...excalidrawAPI.getAppState(),
        selectedElementIds: { [c.elementId]: true },
      } as any,
    });
  };

  const totalOpen = roots.filter((r) => !r.resolved).length;

  return (
    <div className="comments-tab">
      <div className="comments-tab__header">
        <span className="comments-tab__title">
          Comments
          {totalOpen > 0 && (
            <span className="comments-tab__count">{totalOpen}</span>
          )}
        </span>
        <label className="comments-tab__toggle">
          <input
            type="checkbox"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
          />
          Show resolved
        </label>
      </div>

      {!diagramId && (
        <div className="comments-tab__empty">
          Save the diagram to start a thread on it.
        </div>
      )}
      {error && (
        <div className="comments-tab__error">Error: {error}</div>
      )}
      {items === null && diagramId && (
        <div className="comments-tab__empty">Loading…</div>
      )}
      {items && filtered.length === 0 && diagramId && (
        <div className="comments-tab__empty">
          {showResolved ? (
            "No comments yet."
          ) : (
            <>
              No open comments.
              <br />
              Select an element on the canvas and tap 💬 to start a thread.
            </>
          )}
        </div>
      )}

      <ul className="comments-tab__list">
        {filtered.map((root) => {
          const replyList = replies.get(root.id) ?? [];
          const open = isExpanded(root.id, !!root.resolved);
          return (
            <li
              key={root.id}
              className={
                "comments-tab__card" +
                (root.resolved ? " is-resolved" : "") +
                (open ? " is-open" : "")
              }
            >
              <button
                type="button"
                className="comments-tab__head"
                onClick={() => toggle(root.id, !!root.resolved)}
                aria-expanded={open}
              >
                <Avatar name={root.author} />
                <div className="comments-tab__head-text">
                  <div className="comments-tab__meta">
                    <strong>{root.author || "Unknown"}</strong>
                    <span className="comments-tab__time">
                      {formatRelativeTime(root.createdAt)}
                    </span>
                    {replyList.length > 0 && (
                      <span className="comments-tab__replies">
                        {replyList.length}{" "}
                        repl{replyList.length === 1 ? "y" : "ies"}
                      </span>
                    )}
                    {root.resolved && (
                      <span className="comments-tab__pill">resolved</span>
                    )}
                  </div>
                  <div className="comments-tab__text">{root.body}</div>
                </div>
                <span className="comments-tab__chevron" aria-hidden="true">
                  {open ? "▴" : "▾"}
                </span>
              </button>

              {open && (
                <div className="comments-tab__thread">
                  {replyList.map((rp) => (
                    <div key={rp.id} className="comments-tab__reply">
                      <Avatar name={rp.author} size={22} />
                      <div className="comments-tab__reply-body">
                        <div className="comments-tab__reply-meta">
                          <strong>{rp.author || "Unknown"}</strong>
                          <span className="comments-tab__time">
                            {formatRelativeTime(rp.createdAt)}
                          </span>
                        </div>
                        <div className="comments-tab__reply-text">
                          {rp.body}
                        </div>
                      </div>
                    </div>
                  ))}
                  {root.elementId && (
                    <button
                      type="button"
                      className="comments-tab__jump"
                      onClick={(e) => {
                        e.stopPropagation();
                        onJump(root);
                      }}
                    >
                      ↗ Jump to anchor on canvas
                    </button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
